import React, { useState } from "react";
import {
  Edit,
  TabbedForm,
  TextField,
  TextInput,
  FunctionField,
  useGetList,
  useList,
  ListContextProvider,
  Pagination,
  useDataProvider,
  useNotify,
  useRefresh,
  Datagrid,
} from "react-admin";
import {
  Box,
  Typography,
  IconButton,
  FormControl,
  FormLabel,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField as MuiTextField,
} from "@mui/material";
import { useParams } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";

export const OrganizationEdit: React.FC = (props) => {
  const { id } = useParams();
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");

  const { data, total, isLoading } = useGetList(`organizations/${id}/members`, {
    pagination: { page: page, perPage: perPage },
    sort: { field: "id", order: "ASC" },
  });

  const listContext = useList({ data, isLoading, perPage, page });

  const handleRemoveMember = async (memberId: string) => {
    try {
      await dataProvider.delete(`organizations/${id}/members`, {
        id: memberId,
      });
      notify("成员已移除", { type: "success" });
      refresh();
    } catch (error: any) {
      notify(`移除成员失败: ${error.message}`, { type: "error" });
    }
  };

  const handleInviteUser = async () => {
    try {
      await dataProvider.create(`organizations/${id}/invite`, {
        data: { email },
      });
      notify("邀请已发送", { type: "success" });
      setOpen(false);
      setEmail("");
      refresh();
    } catch (error: any) {
      notify(`邀请发送失败: ${error.message}`, { type: "error" });
    }
  };

  return (
    <Edit {...props}>
      <TabbedForm>
        <TabbedForm.Tab label="企业管理">
          <FormControl fullWidth margin="normal">
            <FormLabel>企业名称</FormLabel>
            <TextField source="name" />
          </FormControl>
          <FormControl fullWidth margin="normal">
            <FormLabel>企业别名</FormLabel>
            <TextField source="alias" />
          </FormControl>
          <FormControl fullWidth margin="normal">
            <FormLabel>启用</FormLabel>
            <FunctionField
              render={(record: any) => (record.enabled ? "是" : "否")}
            />
          </FormControl>
          <FormControl fullWidth margin="normal">
            <FormLabel>企业描述</FormLabel>
            <TextInput
              fullWidth
              multiline
              source="description"
              label="组织或分公司描述"
            />
          </FormControl>
          <FormControl fullWidth margin="normal">
            <FormLabel>重定向 URL</FormLabel>
            <TextField source="redirectUrl" />
          </FormControl>
          <FormControl fullWidth margin="normal">
            <FormLabel>属性</FormLabel>
            <FunctionField
              render={(record: any) =>
                record.attributes && Object.keys(record.attributes).length > 0
                  ? JSON.stringify(record.attributes)
                  : "无"
              }
            />
          </FormControl>
          <FormControl fullWidth margin="normal">
            <FormLabel>域名</FormLabel>
            <FunctionField
              render={(record: any) =>
                record.domains && record.domains.length > 0
                  ? record.domains
                      .map(
                        (domain: any) =>
                          `${domain.name} (验证: ${
                            domain.verified ? "是" : "否"
                          })`
                      )
                      .join(", ")
                  : "无"
              }
            />
          </FormControl>
          <Box mt={2}>
            <Typography variant="h6">企业员工</Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpen(true)}
            >
              邀请用户加入组织
            </Button>
          </Box>
          <ListContextProvider value={{ ...listContext, total: total || 0 }}>
            <Datagrid bulkActionButtons={false}>
              <FunctionField
                label="姓名"
                render={(record: any) =>
                  `${record.lastName} ${record.firstName}`
                }
              />
              <TextField source="username" label="用户名" />
              <TextField source="email" label="邮箱" />
              <FunctionField
                label="邮箱验证"
                render={(record: any) => (record.emailVerified ? "是" : "否")}
              />
              <FunctionField
                label="启用"
                render={(record: any) => (record.enabled ? "是" : "否")}
              />
              <FunctionField
                label="创建时间"
                render={(record: any) =>
                  new Date(record.createdTimestamp).toLocaleString("zh-CN", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "numeric",
                    minute: "numeric",
                    second: "numeric",
                  })
                }
              />
              <FunctionField
                label="语言"
                render={(record: any) =>
                  record.attributes?.locale?.join(", ") || "无"
                }
              />
              <FunctionField
                label="操作"
                render={(record: any) => (
                  <IconButton
                    onClick={() => handleRemoveMember(record.id)}
                    color="secondary"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              />
            </Datagrid>
            <Pagination page={page} setPage={setPage} setPerPage={setPerPage} />
          </ListContextProvider>
        </TabbedForm.Tab>
      </TabbedForm>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>邀请用户加入组织</DialogTitle>
        <DialogContent>
          <DialogContentText>
            请输入用户的邮箱地址，我们将发送邀请邮件。
          </DialogContentText>
          <MuiTextField
            autoFocus
            margin="dense"
            label="邮箱地址"
            type="email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">
            取消
          </Button>
          <Button onClick={handleInviteUser} color="primary">
            发送邀请
          </Button>
        </DialogActions>
      </Dialog>
    </Edit>
  );
};